import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import ExportExcel from "../../components/ExportExcel";
import * as XLSX from "xlsx";
import { formatDate } from "../../functions";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { fetchScholarshipDetails } from "../../redux/scholarshipSlice";

function Scholarship() {
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const recordsPerPage = 10;
  const today = new Date().toISOString().split("T")[0];

  const {
    data: scholarshipDetails,
    loading,
    error,
  } = useSelector((state) => state.scholarship);

  useEffect(() => {
    dispatch(fetchScholarshipDetails());
  }, [dispatch]);

  // Filter
  const filteredRecords = scholarshipDetails.filter((scholarship) => {
    const searchLower = searchTerm.toLowerCase();

    return Object.values(scholarship).some((value) => {
      if (typeof value === "string") {
        return value.toLowerCase().includes(searchLower);
      } else if (typeof value === "object" && value !== null) {
        return Object.values(value).some(
          (subValue) =>
            typeof subValue === "string" &&
            subValue.toLowerCase().includes(searchLower)
        );
      }
      return false;
    });
  });

  // ✅ Reset pagination when searching
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // ✅ Apply pagination AFTER filtering
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord
  );

  // Page Navigation
  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const getValueByPath = (obj, path) => {
    return path.split(".").reduce((acc, key) => {
      if (!acc) return "";
      const match = key.match(/(.*?)\[(\d+)\]/);
      if (match) {
        const arrayKey = match[1];
        const index = parseInt(match[2], 10);
        return acc[arrayKey] && acc[arrayKey][index] !== undefined
          ? acc[arrayKey][index]
          : "";
      }
      return acc[key] !== undefined ? acc[key] : "";
    }, obj);
  };

  const columnMapping = {
    "studentDetails.studentId": "Student ID",
    "studentDetails.admission_no": "Admission No",
    "studentDetails.admissionDate": "Admission Date",
    "studentDetails.rollNo": "Roll No",
    "studentDetails.name": "Student Name",
    "studentDetails.gender": "Gender",
    "studentDetails.currentClass": "Class",
    "studentDetails.currentSection": "Section",
    "studentDetails.dateOfBirth": "Date of Birth",
    "studentDetails.phoneNo": "Phone No",
    "studentDetails.house": "House",
    "studentDetails.category": "Category",
    "studentDetails.aadhar_no": "Aadhaar Card No.",
    "studentDetails.bloodGroup": "Blood Group",
    "studentDetails.handicap": "Handicap",
    "transportFacility.busStop": "Bus Stop",
    "transportFacility.vehicleNo": "Vehicle No",
    "transportFacility.busNumber": "Bus No",
    "scholarship.type": "Scholarship Type",
    "scholarship.siblingAdmissionNo": "Sibling Admission No",
    "scholarship.scholarshipFrom": "Scholarship From",
    "scholarship.feeDiscount.monthlyFee": "Monthly Fee",
    "scholarship.feeDiscount.admissionFee": "Admission Fee",
    "scholarship.feeDiscount.developmentCharges": "Development Charges",
  };

  const handleExportExcel = () => {
    ExportExcel(scholarshipDetails, "ScholarshipDetails", columnMapping);
  };

  const handleExportClassWiseExcel = () => {
    if (!columnMapping || Object.keys(columnMapping).length === 0) {
      alert("Column Missing");
      return;
    }

    const workbook = XLSX.utils.book_new();
    const classGroups = {};

    scholarshipDetails.forEach((student) => {
      const cls = student.studentDetails?.currentClass || "Unknown";
      const sec = student.studentDetails?.currentSection || "Unknown";

      if (!classGroups[cls]) {
        classGroups[cls] = {};
      }

      if (!classGroups[cls][sec]) {
        classGroups[cls][sec] = [];
      }

      const formattedItem = {};
      Object.entries(columnMapping).forEach(([key, header]) => {
        let value = getValueByPath(student, key);

        if (Array.isArray(value)) {
          value = value.join(", ");
        }

        if (key === "transportFacility.busStop" && typeof value === "string") {
          value = value.toUpperCase();
        }

        if (
          (key === "studentDetails.dateOfBirth" ||
            key === "studentDetails.admissionDate") &&
          value
        ) {
          value = formatDate(value);
        }

        formattedItem[header] = value;
      });

      classGroups[cls][sec].push(formattedItem);
    });

    Object.entries(classGroups).forEach(([className, sections]) => {
      const combinedData = [];
      const sortedSections = Object.keys(sections).sort();

      sortedSections.forEach((sec) => {
        const students = sections[sec];
        students.sort((a, b) => Number(a["Roll No"]) - Number(b["Roll No"]));
        combinedData.push({ "Student ID": `Section ${sec}` });
        combinedData.push(...students);
        combinedData.push({});
      });

      const worksheet = XLSX.utils.json_to_sheet(combinedData, {
        skipHeader: false,
      });
      XLSX.utils.book_append_sheet(workbook, worksheet, className.slice(0, 31));
    });

    XLSX.writeFile(workbook, `Scholarship_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportTypeWiseExcel = () => {
    if (!columnMapping || Object.keys(columnMapping).length === 0) {
      alert("Column Missing");
      return;
    }

    const workbook = XLSX.utils.book_new();
    const typeGroups = {};

    scholarshipDetails.forEach((student) => {
      const type = student.scholarship?.type || "Unknown";

      if (!typeGroups[type]) {
        typeGroups[type] = [];
      }

      const formattedItem = {};
      Object.entries(columnMapping).forEach(([key, header]) => {
        let value = getValueByPath(student, key);

        // Join array values
        if (Array.isArray(value)) {
          value = value.join(", ");
        }

        // Uppercase Bus Stop
        if (key === "transportFacility.busStop" && typeof value === "string") {
          value = value.toUpperCase();
        }

        // Format date fields
        if (
          (key === "studentDetails.dateOfBirth" ||
            key === "studentDetails.admissionDate") &&
          value
        ) {
          value = formatDate(value);
        }

        formattedItem[header] = value;
      });

      typeGroups[type].push(formattedItem);
    });

    // Create one sheet per scholarship type
    Object.entries(typeGroups).forEach(([type, students]) => {
      students.sort((a, b) => Number(a["Roll No"]) - Number(b["Roll No"]));
      const worksheet = XLSX.utils.json_to_sheet(students, {
        skipHeader: false,
      });
      XLSX.utils.book_append_sheet(workbook, worksheet, type.slice(0, 31));
    });

    XLSX.writeFile(workbook, `Scholarship_TypeWise_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportGenderWiseExcel = () => {
    if (!columnMapping || Object.keys(columnMapping).length === 0) {
      alert("Column Missing");
      return;
    }

    const workbook = XLSX.utils.book_new();
    const genderGroups = {};

    scholarshipDetails.forEach((student) => {
      const gender = student.studentDetails?.gender || "Unknown";

      if (!genderGroups[gender]) {
        genderGroups[gender] = [];
      }

      const formattedItem = {};
      Object.entries(columnMapping).forEach(([key, header]) => {
        let value = getValueByPath(student, key);

        // Join array values
        if (Array.isArray(value)) {
          value = value.join(", ");
        }

        // Uppercase Bus Stop
        if (key === "transportFacility.busStop" && typeof value === "string") {
          value = value.toUpperCase();
        }

        // Format date fields
        if (
          (key === "studentDetails.dateOfBirth" ||
            key === "studentDetails.admissionDate") &&
          value
        ) {
          value = formatDate(value);
        }

        formattedItem[header] = value;
      });

      genderGroups[gender].push(formattedItem);
    });

    // Create one sheet per gender
    Object.entries(genderGroups).forEach(([gender, students]) => {
      students.sort((a, b) => Number(a["Roll No"]) - Number(b["Roll No"]));
      const worksheet = XLSX.utils.json_to_sheet(students, {
        skipHeader: false,
      });
      XLSX.utils.book_append_sheet(workbook, worksheet, gender.slice(0, 31));
    });

    XLSX.writeFile(workbook, `Scholarship_GenderWise_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportHouseWiseExcel = () => {
    if (!columnMapping || Object.keys(columnMapping).length === 0) {
      alert("Column Missing");
      return;
    }

    const workbook = XLSX.utils.book_new();
    const houseGroups = {};

    scholarshipDetails.forEach((student) => {
      const house = student.studentDetails?.house || "Unknown";

      if (!houseGroups[house]) {
        houseGroups[house] = [];
      }

      const formattedItem = {};
      Object.entries(columnMapping).forEach(([key, header]) => {
        let value = getValueByPath(student, key);

        // Join array values
        if (Array.isArray(value)) {
          value = value.join(", ");
        }

        // Uppercase Bus Stop
        if (key === "transportFacility.busStop" && typeof value === "string") {
          value = value.toUpperCase();
        }

        // Format date fields
        if (
          (key === "studentDetails.dateOfBirth" ||
            key === "studentDetails.admissionDate") &&
          value
        ) {
          value = formatDate(value);
        }

        formattedItem[header] = value;
      });

      houseGroups[house].push(formattedItem);
    });

    // Create one sheet per house
    Object.entries(houseGroups).forEach(([house, students]) => {
      students.sort((a, b) => Number(a["Roll No"]) - Number(b["Roll No"]));
      const worksheet = XLSX.utils.json_to_sheet(students, {
        skipHeader: false,
      });
      XLSX.utils.book_append_sheet(workbook, worksheet, house.slice(0, 31));
    });

    XLSX.writeFile(workbook, "Scholarship_HouseWise.xlsx");
    toast.success("Report Generated");
  };

  const handleExportAvailingTransportWiseExcel = () => {
    if (!columnMapping || Object.keys(columnMapping).length === 0) {
      alert("Column Missing");
      return;
    }

    const workbook = XLSX.utils.book_new();

    const transportStudents = scholarshipDetails.filter(
      (student) => !!getValueByPath(student, "transportFacility.busStop")
    );

    if (transportStudents.length === 0) {
      toast.error("No transport students found");
      return;
    }

    const formattedData = transportStudents.map((student) => {
      const row = {};
      Object.entries(columnMapping).forEach(([key, header]) => {
        row[header] = getValueByPath(student, key);
      });
      return row;
    });

    formattedData.sort((a, b) => Number(a["Roll No"]) - Number(b["Roll No"]));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Students");

    XLSX.writeFile(workbook, `Scholarship_AvailingTransport_${today}.xlsx`);
    toast.success("Report Generated");
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div className="">
            <div class="container-fluid my-3 px-3">
              <div className="row g-2">
                <div className="col-lg-8">
                  <div class="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                    <table class="table mb-0 text-nowrap table-centered">
                      <thead class="table-light">
                        <tr>
                          <th className="w-10">No.</th>
                          <th className="lh-1 w-60">
                            Details
                            <i className="bi bi-info-circle ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Displaying Information as per Record
                            </span>
                          </th>
                          <th className="lh-1 w-30">
                            Action
                            <i className="bi bi-person-gear ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Role-Specific Action Panel
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentRecords.map((scholarship, index) => (
                          <tr>
                            <td>{indexOfFirstRecord + index + 1}.</td>
                            <td className="lh-sm py-4 text-wrap">
                              <span class="text-dark">Student ID</span> &mdash;{" "}
                              {scholarship.studentDetails.studentId}
                              <br />
                              <span class="text-dark">Admission No</span>{" "}
                              &mdash; {scholarship.studentDetails.admission_no}{" "}
                              <br />
                              <span class="text-dark">Student Name</span>{" "}
                              &mdash; {scholarship.studentDetails.name} <br />
                              <span class="text-dark">Class</span> &mdash;{" "}
                              {scholarship.studentDetails.currentClass} <br />
                              <span class="text-dark">
                                Section
                              </span> &mdash;{" "}
                              {scholarship.studentDetails.currentSection} <br />
                              <span class="text-dark">
                                Roll No
                              </span> &mdash;{" "}
                              {scholarship.studentDetails.rollNo}
                              <br />
                              <span class="text-dark">
                                Phone No
                              </span> &mdash;{" "}
                              {scholarship.studentDetails.phoneNo}
                              <br />
                              <span class="text-dark">Gender</span> &mdash;{" "}
                              {scholarship.studentDetails.gender}
                              <hr className="w-25" />
                              <span class="text-dark">
                                Stoppage
                              </span> &mdash;{" "}
                              <span className="text-capitalize">
                                {scholarship.transportFacility.busStop || "N/A"}
                              </span>
                              <br />
                              <span class="text-dark">
                                Vehicle No
                              </span> &mdash;{" "}
                              {scholarship.transportFacility.vehicleNo || "N/A"}
                              <br />
                              <span class="text-dark">Bus No</span> &mdash;{" "}
                              {scholarship.transportFacility.busNumber || "N/A"}
                              <hr className="w-25" />
                              <span class="text-dark">
                                Scholarship Type
                              </span>{" "}
                              &mdash;{" "}
                              <span className="text-capitalize">
                                {scholarship.scholarship.type}
                              </span>
                              <br />
                              <span class="text-dark">
                                Scholarship From
                              </span>{" "}
                              &mdash;{" "}
                              <span className="text-capitalize">
                                {scholarship.scholarship.scholarshipFrom}
                              </span>
                              <br />
                              {scholarship.scholarship.type ===
                                "staffChild" && (
                                <>
                                  <span className="text-dark">Employee ID</span>{" "}
                                  &mdash;{" "}
                                  <span className="text-capitalize">
                                    {scholarship.scholarship.employeeId}
                                  </span>
                                  <br />
                                </>
                              )}
                              {scholarship.scholarship.type === "sibling" && (
                                <>
                                  <span className="text-dark">
                                    Sibling Admission No
                                  </span>{" "}
                                  &mdash;{" "}
                                  <span className="text-capitalize">
                                    {scholarship.scholarship.siblingAdmissionNo}
                                  </span>
                                  <br />
                                </>
                              )}
                              <span class="text-dark">Admission Fee</span>{" "}
                              &mdash;{" "}
                              {scholarship.scholarship.feeDiscount.admissionFee}{" "}
                              <br />
                              <span class="text-dark">
                                Monthly Fee
                              </span> &mdash;{" "}
                              {scholarship.scholarship.feeDiscount.monthlyFee}{" "}
                              <br />
                              <span class="text-dark">
                                Development Charges
                              </span>{" "}
                              &mdash;{" "}
                              {
                                scholarship.scholarship.feeDiscount
                                  .developmentCharges
                              }
                            </td>
                            <td>
                              <div className="d-flex flex-column gap-1 width-fit-content">
                                <div className="d-flex align-items-center bg-light rounded-4 mb-2">
                                  <div className="p-2 bg-dark rounded-4 opacity-75">
                                    {!scholarship.transportFacility.busStop ? (
                                      <img
                                        src="/assets/images/walker.png"
                                        width="22"
                                      />
                                    ) : (
                                      <img
                                        src="/assets/images/transport.png"
                                        width="22"
                                      />
                                    )}
                                  </div>
                                  <div className="ps-3">
                                    <p className="mb-0 lh-1 fs-5 text-center text-dark">
                                      {!scholarship.transportFacility.busStop
                                        ? "Walker"
                                        : "Bus"}
                                    </p>
                                  </div>
                                </div>

                                <Link
                                  to="/view-student"
                                  state={{
                                    admissionNo:
                                      scholarship.studentDetails.admission_no,
                                  }}
                                  class="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  View Details
                                </Link>
                                <Link
                                  to="/update-student"
                                  state={{
                                    admissionNo:
                                      scholarship.studentDetails.admission_no,
                                  }}
                                  class="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Update Details
                                </Link>
                                <Link
                                  to="/assign-view-incidents"
                                  state={{
                                    admissionNo:
                                      scholarship.studentDetails.admission_no,
                                  }}
                                  class="badge bg-dark-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Behaviour Records
                                </Link>
                                <Link
                                  to="/update-transport"
                                  state={{
                                    admissionNo:
                                      scholarship.studentDetails.admission_no,
                                  }}
                                  class="badge bg-info-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Transport
                                </Link>
                                <Link
                                  to="/update-scholarship"
                                  state={{
                                    admissionNo:
                                      scholarship.studentDetails.admission_no,
                                  }}
                                  class="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Scholarship
                                </Link>
                                <Link
                                  to="/collect-fees"
                                  state={{
                                    admissionNo:
                                      scholarship.studentDetails.admission_no,
                                  }}
                                  class="badge bg-warning-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Collect Fees
                                </Link>
                                <Link
                                  to="/student-attendance"
                                  state={{
                                    admissionNo:
                                      scholarship.studentDetails.admission_no,
                                  }}
                                  class="badge bg-dark-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Attendance
                                </Link>
                                <Link
                                  to="/book-issue-return-student"
                                  state={{
                                    admissionNo:
                                      scholarship.studentDetails.admission_no,
                                  }}
                                  class="badge bg-dark-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Book Issue / Return
                                </Link>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="col-lg-4">
                  <div class="">
                    <div className="mb-3">
                      <div className="bg-white rounded-pill">
                        <form
                          className="d-flex align-items-center"
                          onSubmit={(e) => e.preventDefault()}
                        >
                          <span className="position-absolute ps-3 search-icon">
                            <i className="bi bi-filter-circle-fill ms-1" />
                          </span>
                          <input
                            type="search"
                            className="form-control ps-7 rounded-3 border-0 rounded-4"
                            placeholder="Filter Data"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </form>
                      </div>
                      <div className="mt-2 ms-4">
                        <nav aria-label="breadcrumb">
                          <ol class="breadcrumb pe-3">
                            <li class="breadcrumb-item">
                              <a href="#">Student Information</a>
                            </li>
                            <li
                              class="breadcrumb-item active"
                              aria-current="page"
                            >
                              Scholarship
                            </li>
                          </ol>
                        </nav>
                      </div>
                    </div>

                    <div className="p-4 bg-white rounded-4">
                      <div class="bg-danger-soft rounded-4 p-3 d-flex align-items-center">
                        <div class="avatar-group">
                          {[1, 2, 3, 4, 5].map((num) => (
                            <span className="avatar avatar-md" key={num}>
                              <img
                                alt={`avatar-${num}`}
                                src={`/assets/images/students/${num}.jpg`}
                                className="rounded-circle"
                              />
                            </span>
                          ))}
                        </div>
                        <h3 className="fw-bold m-0 text-center fs-4 ms-3">
                          {scholarshipDetails.length.toLocaleString("en-IN")}
                        </h3>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="d-flex align-items-center">
                          {loading ? (
                            <div
                              className="spinner-border text-dark data-loader-spinner"
                              role="status"
                            ></div>
                          ) : (
                            <img src="/assets/images/folder.svg" />
                          )}

                          <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                            Scholarship
                            <br />
                            <span className="text-secondary fs-6 fw-medium lh-sm">
                              2.4 MB Size
                            </span>
                          </p>
                        </div>
                        <span
                          onClick={handleExportExcel}
                          className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer"
                        >
                          Export
                        </span>
                      </div>

                      <div class="px-4 py-2 mt-3 bg-light rounded-4">
                        <li className="list-group-item px-0">
                          <a
                            className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                            data-bs-toggle="collapse"
                            href="#specifications"
                            role="button"
                            aria-expanded="false"
                            aria-controls="specifications"
                          >
                            <p className="m-0 fw-medium lh-sm text-dark">
                              Download Report
                            </p>
                            <span className="chevron-arrow ms-4">
                              <i className="bi bi-chevron-down fs-4" />
                            </span>
                          </a>
                          <div
                            className="collapse"
                            id="specifications"
                            data-bs-parent="#ecommerceAccordion"
                          >
                            <div className="py-3">
                              <div class="rounded-4 d-flex flex-wrap">
                                <span
                                  onClick={handleExportClassWiseExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Class/Section Wise
                                </span>
                                <span
                                  onClick={handleExportTypeWiseExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Scholarship Type Wise
                                </span>
                                <span
                                  onClick={handleExportGenderWiseExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Gender Wise
                                </span>
                                <span
                                  onClick={handleExportHouseWiseExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  House - Wise Report
                                </span>
                                <span
                                  onClick={
                                    handleExportAvailingTransportWiseExcel
                                  }
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Availing Transport Report
                                </span>
                              </div>
                            </div>
                          </div>
                        </li>
                      </div>
                    </div>

                    <div className="my-4">
                      <nav>
                        <ul className="pagination justify-content-center mb-0">
                          <li
                            className={`page-item ${
                              currentPage === 1 ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={prevPage}>
                              <i className="bi bi-chevron-left"></i>
                            </button>
                          </li>

                          {(() => {
                            const pageNumbers = [];
                            pageNumbers.push(1);

                            if (currentPage > 3) {
                              pageNumbers.push("left-ellipsis");
                            }

                            if (
                              currentPage !== 1 &&
                              currentPage !== totalPages
                            ) {
                              pageNumbers.push(currentPage);
                            }

                            if (currentPage < totalPages - 2) {
                              pageNumbers.push("right-ellipsis");
                            }

                            if (totalPages > 1) {
                              pageNumbers.push(totalPages);
                            }

                            return pageNumbers.map((page, index) => {
                              if (
                                page === "left-ellipsis" ||
                                page === "right-ellipsis"
                              ) {
                                return (
                                  <li
                                    key={index}
                                    className="page-item disabled"
                                  >
                                    <span className="page-link">...</span>
                                  </li>
                                );
                              }

                              return (
                                <li
                                  key={index}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              );
                            });
                          })()}

                          <li
                            className={`page-item ${
                              currentPage === totalPages ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={nextPage}>
                              <i className="bi bi-chevron-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default Scholarship;
